"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import {
  fadeUp,
  stagger,
  cardItem,
  fastStagger,
  viewportOnce,
} from "../components/motionVariants";
import {
  BookOpen,
  BarChart3,
  FileText,
  BarChart2,
  Truck,
  Package,
  QrCode,
  UserCog,
  RefreshCw,
  TrendingUp,
  Zap,
  Headphones,
  ArrowRight,
} from "lucide-react";

// ─── Products ─────────────────────────────────────────────────────────
const PRODUCTS = [
  {
    id: "books",
    label: "ABSS Books",
    Icon: BookOpen,
    color: "#171717",
    bg: "#f5f5f5",
    tagline: "Accounting, GST & Inventory",
    href: "/documentation/books/getting-started/welcome",
    desc: "Complete documentation for the Books platform - sales, purchase, inventory, production, reports, and GST compliance.",
    sections: [
      {
        label: "Getting Started",
        href: "/documentation/books/getting-started/welcome",
      },
      {
        label: "Dashboard",
        href: "/documentation/books/home/dashboard",
      },
      {
        label: "Sales Module",
        href: "/documentation/books/sales/customer-master",
      },
      {
        label: "Purchase Module",
        href: "/documentation/books/purchase/vendors",
      },
      {
        label: "Accountant Module",
        href: "/documentation/books/accountant/chart-of-accounts",
      },
      {
        label: "Inventory",
        href: "/documentation/books/inventory/view-inventory",
      },
      {
        label: "Production",
        href: "/documentation/books/production/add-production",
      },
      {
        label: "Reports",
        href: "/documentation/books/reports/reports-overview",
      },
      {
        label: "GST & Compliance",
        href: "/documentation/books/sales/invoices",
      },
      { label: "Masters", href: "/documentation/books/all-master/item-master" },
      {
        label: "Settings",
        href: "/documentation/books/settings/my-profile",
      },
      {
        label: "Support",
        href: "/documentation/books/support/support",
      },
    ],
  },
  {
    id: "crm",
    label: "ABSS CRM",
    Icon: BarChart3,
    color: "#171717",
    bg: "#f5f5f5",
    tagline: "Leads, Sales & Customer Management",
    href: "/documentation/crm/getting-started/about",
    desc: "Documentation for the CRM platform - leads, campaigns, quotations, sales orders, customer orders, and roles.",
    sections: [
      { label: "Getting Started", href: "/documentation/crm/getting-started" },
      { label: "Dashboard", href: "/documentation/crm/dashboard" },
      { label: "Leads & Customers", href: "/documentation/crm/leads" },
      { label: "Campaigns", href: "/documentation/crm/campaigns" },
      { label: "Quotations", href: "/documentation/crm/quotations" },
      { label: "Sales Orders", href: "/documentation/crm/sales-orders" },
      { label: "Customer Orders", href: "/documentation/crm/customer-orders" },
      { label: "Users & Roles", href: "/documentation/crm/users" },
    ],
  },
];

// ─── Popular Articles ─────────────────────────────────────────────────
const POPULAR = [
  {
    Icon: FileText,
    title: "Setting Up E-Invoice (IRN) in ABSS Books",
    category: "GST & Compliance",
    href: "/blog-details",
  },
  {
    Icon: BarChart2,
    title: "Setting Up GST Rates and HSN Codes",
    category: "GST & Compliance",
    href: "/blog-details",
  },
  {
    Icon: Truck,
    title: "Generating an E-Way Bill from a Delivery Challan",
    category: "GST & Compliance",
    href: "/blog-details",
  },
  {
    Icon: Package,
    title: "Setting Up Multiple Warehouses in ABSS Books",
    category: "Inventory",
    href: "/blog-details",
  },
  {
    Icon: QrCode,
    title: "Configuring QR Lead Capture for Trade Shows",
    category: "CRM",
    href: "/blog-details",
  },
  {
    Icon: UserCog,
    title: "Creating Custom Roles and Permissions",
    category: "Settings",
    href: "/blog-details",
  },
  {
    Icon: RefreshCw,
    title: "Migrating Data from Tally to ABSS Books",
    category: "Onboarding",
    href: "/blog-details",
  },
  {
    Icon: TrendingUp,
    title: "Understanding the Debtors Aging Report",
    category: "Reports",
    href: "/blog-details",
  },
];

// ─── Books Module Map ────────────────────────────────────────────────
const BOOKS_MODULES = [
  {
    category: "Home",
    color: "#171717",
    links: [
      { label: "Dashboard", href: "/documentation/books/home/dashboard" },
    ],
  },
  {
    category: "Sales",
    color: "#171717",
    links: [
      {
        label: "Customer Master",
        href: "/documentation/books/sales/customer-master",
      },
      { label: "Quotations", href: "/documentation/books/sales/quotes" },
      {
        label: "Customer Orders",
        href: "/documentation/books/sales/customer-orders",
      },
      {
        label: "Sales Orders",
        href: "/documentation/books/sales/sales-orders",
      },
      {
        label: "Delivery Challans",
        href: "/documentation/books/sales/delivery-challans",
      },
      {
        label: "Invoices + E-Invoice",
        href: "/documentation/books/sales/invoices",
      },
      {
        label: "Payments Received",
        href: "/documentation/books/sales/payments-received",
      },
      {
        label: "Recurring Invoices",
        href: "/documentation/books/sales/recurring-invoices",
      },
      {
        label: "Credit Notes",
        href: "/documentation/books/sales/credit-notes",
      },
    ],
  },
  {
    category: "Purchase",
    color: "#171717",
    links: [
      { label: "Vendors", href: "/documentation/books/purchase/vendors" },
      {
        label: "Purchase Orders",
        href: "/documentation/books/purchase/purchase-orders",
      },
      { label: "GRPO", href: "/documentation/books/purchase/grpo" },
      { label: "Bills", href: "/documentation/books/purchase/bills" },
      {
        label: "Payments Made",
        href: "/documentation/books/purchase/payments-made",
      },
      { label: "Expenses", href: "/documentation/books/purchase/expenses" },
      {
        label: "Recurring Bills",
        href: "/documentation/books/purchase/recurring-bills",
      },
      {
        label: "Debit Notes",
        href: "/documentation/books/purchase/debit-notes",
      },
    ],
  },
  {
    category: "Accountant",
    color: "#171717",
    links: [
      {
        label: "Chart of Accounts",
        href: "/documentation/books/accountant/chart-of-accounts",
      },
      {
        label: "Account Ledger",
        href: "/documentation/books/accountant/account-ledger",
      },
      {
        label: "Manual Journals",
        href: "/documentation/books/accountant/manual-journals",
      },
    ],
  },
  {
    category: "Inventory",
    color: "#171717",
    links: [
      {
        label: "View Inventory",
        href: "/documentation/books/inventory/view-inventory",
      },
      {
        label: "Inventory Transfer",
        href: "/documentation/books/inventory/inventory-transfer",
      },
      {
        label: "Inventory Adjustment",
        href: "/documentation/books/inventory/inventory-adjustment",
      },
    ],
  },
  {
    category: "Production",
    color: "#171717",
    links: [
      {
        label: "Add Production",
        href: "/documentation/books/production/add-production",
      },
      {
        label: "Submit Production",
        href: "/documentation/books/production/submit-production",
      },
    ],
  },
  {
    category: "Reports",
    color: "#171717",
    links: [
      {
        label: "Reports Overview",
        href: "/documentation/books/reports/reports-overview",
      },
    ],
  },
  {
    category: "Analytics",
    color: "#171717",
    links: [
      {
        label: "Analytics Dashboard",
        href: "/documentation/books/analytics/analytics-overview",
      },
    ],
  },
  {
    category: "Masters",
    color: "#171717",
    links: [
      {
        label: "Item Master",
        href: "/documentation/books/all-master/item-master",
      },
      { label: "Branch", href: "/documentation/books/all-master/branch" },
      {
        label: "Warehouse",
        href: "/documentation/books/all-master/warehouse-master",
      },
      {
        label: "Price List",
        href: "/documentation/books/all-master/price-list",
      },
      { label: "Recipe / BOM", href: "/documentation/books/all-master/recipe" },
      {
        label: "Sales Order Tag",
        href: "/documentation/books/all-master/sales-order-tag",
      },
    ],
  },
  {
    category: "Settings",
    color: "#171717",
    links: [
      { label: "My Profile", href: "/documentation/books/settings/my-profile" },
      { label: "Profile", href: "/documentation/books/settings/profile" },
      { label: "Currencies", href: "/documentation/books/settings/currencies" },
      {
        label: "WhatsApp Config",
        href: "/documentation/books/settings/whatsapp-config",
      },
      {
        label: "GST Portal Credentials",
        href: "/documentation/books/settings/gst-portal-credentials",
      },
      {
        label: "Manage Subscription",
        href: "/documentation/books/settings/manage-subscription",
      },
      { label: "Users", href: "/documentation/books/settings/users" },
      { label: "Roles", href: "/documentation/books/settings/roles" },
      {
        label: "Quotation Config",
        href: "/documentation/books/settings/quotation-config",
      },
      {
        label: "Inventory Config",
        href: "/documentation/books/settings/inventory-config",
      },
      {
        label: "Additional Charges",
        href: "/documentation/books/settings/additional-charges",
      },
      { label: "Discounts", href: "/documentation/books/settings/discounts" },
      {
        label: "Credit Limits",
        href: "/documentation/books/settings/credit-limits",
      },
      {
        label: "Organization Defaults",
        href: "/documentation/books/settings/organization-defaults",
      },
      {
        label: "Notifications",
        href: "/documentation/books/settings/notifications",
      },
    ],
  },
  {
    category: "Customer Support",
    color: "#171717",
    links: [
      { label: "Support", href: "/documentation/books/support/support" },
    ],
  },
];

// ─── Quick start CTA cards ───────────────────────────────────────────
const CTA_CARDS = [
  {
    Icon: Zap,
    iconColor: "#171717",
    iconBg: "#f5f5f5",
    title: "New to ABSS?",
    desc: "Start with our Getting Started guide - from account setup to your first GST invoice in 30 minutes.",
    cta: "Quick Start Guide",
    href: "/documentation/quick-start",
  },
  {
    Icon: RefreshCw,
    iconColor: "#171717",
    iconBg: "#f5f5f5",
    title: "Migrating from Tally?",
    desc: "Our step-by-step migration guide covers customer master, item master, and historical balance import.",
    cta: "Migration Guide",
    href: "/documentation/books/getting-started/welcome",
  },
  {
    Icon: Headphones,
    iconColor: "#171717",
    iconBg: "#f5f5f5",
    title: "Need human help?",
    desc: "Our India-based support team is available Mon–Fri, 10 AM–6 PM IST via chat, email, and WhatsApp.",
    cta: "Contact Support",
    href: "/contact",
  },
];

const sectionPadding = "px-5 sm:px-6 md:px-10 lg:px-12";

export default function DocumentationPage() {
  const router = useRouter();

  return (
    <div className="overflow-x-hidden w-full min-h-screen bg-white">
      {/* ── HERO ─────────────────────────────────────────────────── */}
      <section className="relative w-full bg-white border-b border-[#E5E5E5] overflow-hidden">
        <div className="absolute inset-0 z-0 select-none pointer-events-none">
          <div className="absolute top-[-25%] left-[-15%] w-[60%] h-[80%] rounded-full bg-gradient-to-br from-orange-200/40 to-amber-100/10 blur-[130px] animate-aurora-pulse" />
          <div className="absolute bottom-[-15%] right-[-10%] w-[55%] h-[75%] rounded-full bg-gradient-to-tr from-blue-200/30 to-indigo-100/10 blur-[130px] animate-aurora-pulse [animation-delay:3s]" />
          <div className="absolute inset-0 bg-grid-mesh opacity-80" />
        </div>

        <div
          className={`relative z-10 max-w-[1160px] mx-auto pt-24 sm:pt-28 md:pt-32 pb-10 sm:pb-16 lg:pb-16 ${sectionPadding} text-center min-w-0`}
        >
          <motion.div
            variants={stagger}
            initial="hidden"
            animate="show"
            className="max-w-4xl mx-auto w-full min-w-0"
          >
            <motion.div
              variants={fadeUp}
              className="flex justify-center mb-5 sm:mb-6"
            >
              <span className="inline-flex items-center gap-1.5 border border-[#e5e5e5] rounded-full px-3.5 sm:px-4 py-1.5 text-[12px] sm:text-[13px] text-[#262626] geist-semibold bg-white/80 backdrop-blur-sm shadow-sm">
                <span className="w-2 h-2 rounded-full bg-[#DF5D01] inline-block animate-pulse"></span>
                Help Center
              </span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              className="text-[26px] sm:text-[48px] md:text-[60px] lg:text-[66px] leading-[1.15] sm:leading-[1.12] text-[#171717] mb-4 sm:mb-6 sentient-bold tracking-tight px-1"
            >
              ABSS Help Center
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="text-[15px] sm:text-lg md:text-[21px] font-medium leading-relaxed text-[#262626] max-w-[640px] mx-auto geist-medium px-1 mb-7 sm:mb-8"
            >
              Step-by-step guides for every module in ABSS Books and ABSS CRM.
              Find answers in seconds.
            </motion.p>

          </motion.div>
        </div>
      </section>

      {/* ── PRODUCT CARDS ─────────────────────────────────────────── */}
      <section className="w-full bg-[#f5f5f5] border-b border-[#E5E5E5] overflow-hidden">
        <div
          className={`max-w-[1160px] mx-auto ${sectionPadding} py-10 sm:py-16 md:py-20 min-w-0`}
        >
          <motion.div
            className="grid md:grid-cols-2 gap-4 sm:gap-8 min-w-0"
            variants={stagger}
            {...viewportOnce}
          >
            {PRODUCTS.map((product) => (
              <motion.div
                key={product.id}
                variants={cardItem}
                role="link"
                tabIndex={0}
                onClick={() => router.push(product.href)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    router.push(product.href);
                  }
                }}
                className="bg-white border border-[#e5e5e5] rounded-xl sm:rounded-3xl p-4 sm:p-8 shadow-md sm:shadow-lg hover:shadow-xl hover:border-neutral-300/80 transition-all duration-500 cursor-pointer group min-w-0"
              >
                <div className="flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-3 sm:gap-5 mb-4 sm:mb-6">
                  <div
                    className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm"
                    style={{ backgroundColor: product.bg }}
                  >
                    <product.Icon
                      className="w-5 h-5 sm:w-6 sm:h-6"
                      style={{ color: product.color }}
                    />
                  </div>
                  <div className="min-w-0 w-full">
                    <span className="inline-flex items-center gap-1.5 border border-[#e5e5e5] rounded-full px-2.5 sm:px-3 py-1 text-[10px] sm:text-[11px] geist-semibold bg-[#FAFAFA] text-[#525252] shadow-sm mb-2">
                      {product.tagline}
                    </span>
                    <h2 className="text-[20px] sm:text-3xl md:text-[34px] leading-[1.25] text-[#171717] sentient-medium tracking-tight">
                      {product.label}
                    </h2>
                  </div>
                </div>
                <p className="text-[#333333] text-[13px] sm:text-[15px] md:text-[16px] leading-relaxed geist-medium mb-5 sm:mb-7 text-center sm:text-left">
                  {product.desc}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 sm:gap-2">
                  {product.sections.map((section, i) => (
                    <Link
                      key={i}
                      href={section.href}
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center gap-2 text-[13px] sm:text-[15px] text-[#333333] geist-medium hover:text-black transition-colors py-1 min-w-0"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-[#D4D4D4] flex-shrink-0" />
                      <span className="truncate">{section.label}</span>
                    </Link>
                  ))}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── POPULAR ARTICLES ──────────────────────────────────────── */}
      <section className="w-full bg-white border-b border-[#E5E5E5] overflow-hidden">
        <div
          className={`max-w-[1160px] mx-auto ${sectionPadding} py-10 sm:py-16 md:py-20 min-w-0`}
        >
          <motion.div
            className="mb-8 sm:mb-14 text-center md:text-left"
            variants={stagger}
            {...viewportOnce}
          >
            <motion.p
              variants={fadeUp}
              className="text-[#DF5D01] text-[13px] sm:text-[14px] geist-medium mb-2.5 sm:mb-3"
            >
              Popular Guides
            </motion.p>
            <motion.h2
              variants={fadeUp}
              className="text-[22px] sm:text-[38px] md:text-[44px] leading-[1.25] sm:leading-[1.2] text-[#171717] sentient-medium"
            >
              Most Read Documentation
            </motion.h2>
          </motion.div>

          <motion.div
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 min-w-0"
            variants={fastStagger}
            {...viewportOnce}
          >
            {POPULAR.map((article, i) => (
              <motion.div key={i} variants={cardItem} className="min-w-0">
                <Link
                  href={article.href}
                  className="flex flex-col h-full border border-[#E5E5E5]/80 rounded-xl sm:rounded-2xl bg-white/95 backdrop-blur-sm p-4 sm:p-6 group hover:border-neutral-300/80 hover:shadow-lg transition-all duration-300 shadow-sm min-w-0"
                >
                  <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-[#FAFAFA] border border-[#E5E5E5] flex items-center justify-center mb-3 sm:mb-4 flex-shrink-0 shadow-sm">
                    <article.Icon className="w-4 h-4 sm:w-5 sm:h-5 text-[#525252] group-hover:text-[#171717] transition-colors" />
                  </div>
                  <h3 className="text-[14px] sm:text-base font-semibold text-[#171717] geist-semibold tracking-tight leading-relaxed mb-2.5 sm:mb-3 flex-1 group-hover:text-black transition-colors">
                    {article.title}
                  </h3>
                  <span className="text-[12px] sm:text-[13px] text-[#737373] geist-medium">
                    {article.category}
                  </span>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── BOOKS FULL MODULE MAP ─────────────────────────────────── */}
      <section className="w-full bg-[#f5f5f5] border-b border-[#E5E5E5] overflow-hidden">
        <div
          className={`max-w-[1160px] mx-auto ${sectionPadding} py-10 sm:py-16 md:py-20 min-w-0`}
        >
          <motion.div
            className="mb-8 sm:mb-14 text-center md:text-left"
            variants={stagger}
            {...viewportOnce}
          >
            <motion.p
              variants={fadeUp}
              className="text-[#DF5D01] text-[13px] sm:text-[14px] geist-medium mb-2.5 sm:mb-3"
            >
              ABSS Books
            </motion.p>
            <motion.h2
              variants={fadeUp}
              className="text-[22px] sm:text-[38px] md:text-[44px] leading-[1.25] sm:leading-[1.2] text-[#171717] sentient-medium"
            >
              Complete Books Module Index
            </motion.h2>
          </motion.div>

          <motion.div
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 min-w-0"
            variants={stagger}
            {...viewportOnce}
          >
            {BOOKS_MODULES.map((group, gi) => (
              <motion.div
                key={gi}
                variants={cardItem}
                className="bg-white border border-[#e5e5e5] rounded-xl sm:rounded-2xl overflow-hidden shadow-sm hover:shadow-lg hover:border-neutral-300/80 transition-all duration-300 min-w-0"
              >
                <div
                  className="px-4 sm:px-5 py-3 sm:py-4 border-b border-[#E5E5E5] flex items-center gap-3"
                  style={{
                    borderLeftWidth: "3px",
                    borderLeftColor: group.color,
                  }}
                >
                  <span className="text-[14px] sm:text-base font-semibold text-[#171717] geist-semibold">
                    {group.category}
                  </span>
                </div>
                <ul className="px-4 sm:px-5 py-3 sm:py-4 space-y-2 sm:space-y-2.5">
                  {group.links.map((link, li) => (
                    <li key={li} className="min-w-0">
                      <Link
                        href={link.href}
                        className="text-[13px] sm:text-[15px] text-[#333333] geist-medium hover:text-black transition-colors flex items-center gap-2 min-w-0"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-[#D4D4D4] flex-shrink-0" />
                        <span className="truncate">{link.label}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── QUICK START CTA ───────────────────────────────────────── */}
      <section className="w-full bg-white border-b border-[#E5E5E5] overflow-hidden">
        <div
          className={`max-w-[1160px] mx-auto ${sectionPadding} py-10 sm:py-16 md:py-20 min-w-0`}
        >
          <motion.div
            className="grid md:grid-cols-3 gap-4 sm:gap-8 min-w-0"
            variants={stagger}
            {...viewportOnce}
          >
            {CTA_CARDS.map((card, i) => (
              <motion.div
                key={i}
                variants={cardItem}
                className="border border-[#E5E5E5]/80 rounded-xl sm:rounded-2xl bg-white/95 backdrop-blur-sm p-4 sm:p-8 flex flex-col shadow-sm hover:shadow-lg hover:border-neutral-300/80 transition-all duration-300 min-w-0 text-center md:text-left items-center md:items-start"
              >
                <div
                  className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center mb-4 sm:mb-5 flex-shrink-0 shadow-sm"
                  style={{ backgroundColor: card.iconBg }}
                >
                  <card.Icon
                    className="w-4 h-4 sm:w-5 sm:h-5"
                    style={{ color: card.iconColor }}
                  />
                </div>
                <h3 className="text-[18px] sm:text-[22px] md:text-[24px] text-[#171717] mb-2.5 sm:mb-3 sentient-medium">
                  {card.title}
                </h3>
                <p className="text-[#333333] text-[13px] sm:text-[15px] md:text-[16px] leading-relaxed geist-medium mb-5 sm:mb-6 flex-1 max-w-[320px] md:max-w-none">
                  {card.desc}
                </p>
                <Link
                  href={card.href}
                  className="inline-flex items-center justify-center md:justify-start gap-1.5 geist-semibold text-sm text-black hover:gap-2.5 transition-all"
                >
                  {card.cta} <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
